import { useEffect, useRef, useState } from "react";

import { API_URL } from "@/lib/env";

/** 後処理の残り。サーバが数えて送ってくる。 */
export interface DrawingProgress {
  pending_thumbnails: number;
  pending_ocr: number;
  /**
   * まとめて依頼した QR 押印の残り。**QR を扱わない画面（見積）では来ない**
   * ので省略可。
   */
  pending_qr?: number;
}

/**
 * ブラウザが諦めたあと、こちらから繋ぎ直すまでの待ち（ミリ秒）。
 *
 * 失敗が続く間は倍にしていく。原因がセッション切れなら、人がログインし直すまで
 * 何度試しても通らないので、短い間隔で叩き続けても意味が無い。
 */
const RETRY_MS = 5_000;
const MAX_RETRY_MS = 60_000;

/**
 * 図面の後処理（サムネイル・図番）の進み具合を受け取る。
 *
 * サムネイルは API が、図番は別プロセスのワーカーが、どちらも応答を返したあとに
 * 作る。以前は一覧を 2 秒おきに取り直して待っていたが、
 *
 * - 出来ていても最大 2 秒遅れて出る
 * - 自分が仕掛けたぶんを待つために「60 秒だけ問い合わせる」という決め打ちが要り、
 *   それを超えると出来ていないように見える（42 ページの取り込みで実際に起きた）
 *
 * という具合の悪さがあった。サーバから押し出してもらえば、どちらも要らなくなる。
 *
 * **WebSocket ではなく `EventSource` を使う。** 向きが一方向で足り、切れたときの
 * 繋ぎ直しをブラウザがやってくれる。認証は Cookie なので `withCredentials` を
 * 付ける（API は別オリジンにある）。
 *
 * ## 切れたときにどう戻るか
 *
 * 実際のブラウザで測った挙動は 2 通りに分かれる。
 *
 * - **ストリームがサーバ側で閉じられた場合**（30 分の上限、ALB の idle timeout、
 *   デプロイ、回線の瞬断）: `readyState` は `CONNECTING` になり、**ブラウザが
 *   約 3 秒後に自分で繋ぎ直す**。こちらは何もしなくてよい
 * - **応答が 2xx でなかった場合**（セッション切れの 401 など）: `readyState` は
 *   `CLOSED` になり、**二度と繋ぎ直さない**。ここだけは自分で繋ぎ直す必要がある
 *
 * 後者を放っておくと、画面は開いているのに更新だけが黙って止まる。`onerror` で
 * `CLOSED` を見分け、間隔を空けて張り直す。
 *
 * 繋ぎ直したときの取りこぼしは**サーバが接続直後に必ず現状を 1 通送る**ことで
 * 埋まる。切れている間に終わった処理も、繋ぎ直した時点の件数として届く。
 *
 * 返すのは**残り件数**で、一覧そのものは来ない。並べ替えや絞り込みの都合が
 * サーバと画面に分かれないようにするため、知らせを受けたら `onProgress` の中で
 * 一覧を取り直す。
 */
export function useDrawingEvents(
  /** SSE の経路（`/quotations/12/drawings/events` など）。`API_URL` からの相対。 */
  path: string,
  onProgress: (progress: DrawingProgress) => void,
  enabled = true,
): DrawingProgress | null {
  const [progress, setProgress] = useState<DrawingProgress | null>(null);
  // 毎描画で作り直される関数を依存に入れると購読を張り直し続ける。最新のものを
  // 参照だけ差し替えて、購読そのものは経路が変わるまで保つ。
  const handler = useRef(onProgress);
  handler.current = onProgress;

  useEffect(() => {
    if (!enabled || !API_URL) return;

    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = RETRY_MS;
    // 画面から外れたあとに繋ぎ直してしまわないための印。
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const current = new EventSource(`${API_URL}${path}`, {
        withCredentials: true,
      });
      source = current;

      current.addEventListener("progress", (event) => {
        // 届いたということは通っている。次に切れたときは短い待ちから始める。
        delay = RETRY_MS;
        let next: DrawingProgress;
        try {
          next = JSON.parse((event as MessageEvent<string>).data) as DrawingProgress;
        } catch {
          // 壊れた 1 通で購読ごと落とさない。次の知らせで追いつく。
          return;
        }
        setProgress(next);
        handler.current(next);
      });

      current.onerror = () => {
        // CONNECTING ならブラウザが繋ぎ直す。任せたほうが早い。
        if (current.readyState !== EventSource.CLOSED) return;
        // CLOSED はブラウザが諦めた合図（2xx 以外が返ったとき）。
        current.close();
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, MAX_RETRY_MS);
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      source?.close();
    };
  }, [path, enabled]);

  return progress;
}
